import type { LucideIcon } from 'lucide-react';
import { MoreHorizontal } from 'lucide-react';
import type * as React from 'react';
import Spinner from '@/components/shared/Spinner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export interface SimpleTableActionItem {
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
  disabled?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  destructive?: boolean;
}

interface SimpleTableActionsDropdownProps {
  actions: SimpleTableActionItem[];
  label?: string;
}

export const SimpleTableActionsDropdown = ({
  actions,
  label = 'Open menu',
}: SimpleTableActionsDropdownProps) => {
  const visibleActions = actions.filter(Boolean);
  if (!visibleActions.length) return null;

  const stopRowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div className="flex justify-end" onClick={stopRowClick}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">{label}</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="z-10000">
          {visibleActions.map((action) => {
            const Icon = action.icon;
            const isDisabled = action.disabled || action.loading;

            return (
              <DropdownMenuItem
                key={action.label}
                disabled={isDisabled}
                onSelect={() => {
                  if (!isDisabled) action.onSelect();
                }}
                className={cn(
                  'flex items-center gap-2',
                  action.destructive && 'text-red-600 focus:text-red-600',
                )}
              >
                {action.loading ? <Spinner size="sm" /> : <Icon className="h-4 w-4" />}
                {action.loading ? action.loadingLabel ?? `${action.label}...` : action.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
